import { useCallback, useEffect, useMemo, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import {
  fromKg,
  normalizeWasteUnit
} from "../../../../../../components/wasteManagment/wasteManagement.utils";
import { WEIGHT_UNIT_TYPES_OPTIONs_WITH_GRAM } from "../../../../../../config/constants";
import { MEASUREMENT_TYPES } from "../../../../../../config/enums/MeasurementTypes";
import { PackagingType } from "../../../../../../config/enums/PackagingType.enum";
import { getPackagingLevelMeta, isUnitSaved } from "../packagingLevel.config";
import {
  PackagingLevelProps,
  PackagingLevelSectionProps,
  PackagingLevelUnitSectionProps
} from "../packagingLevel.types";
import { calculateBaseUnitTotalWeightKg } from "../utils/calculateBaseUnitTotalWeight";
import { usePackagingUnitSave } from "./usePackagingUnitSave";

interface UsePackagingLevelUnitOptions extends PackagingLevelProps {
  form: UseFormReturn<any>;
}

export function usePackagingLevelUnit({
  form,
  packagingType,
  unitSizes,
  weightUnit = MEASUREMENT_TYPES.WEIGHT_KG,
  palletLength,
  palletWidth,
  unitSizeWeightUnit,
  setUnitSizeWeightUnit,
  updatePackingDetails,
  changeUnitsType,
  setIsSaveBtnClicked,
  isSaveBtnClicked,
  setClickedPackagingUnit,
  clickedPackagingUnit,
  packagingName,
  lockStatus = false,
  totalWeightOfItem = 0,
  packagingData = [],
  mutations
}: UsePackagingLevelUnitOptions) {
  const [accordionEnabled, setAccordionEnabled] = useState(false);
  const [unitEditMode, setUnitEditMode] = useState(false);
  const [materialsUnlocked, setMaterialsUnlocked] = useState(false);

  const levelMeta = getPackagingLevelMeta(packagingType);
  const fieldName = levelMeta?.name ?? "baseUnit";
  const { isLoading } = mutations;

  const {
    formState: { errors: errorsUnit },
    control,
    setValue,
    watch
  } = form;

  const unitSaved = isUnitSaved(packagingType, unitSizes, palletWidth, palletLength);
  const unitFieldsLocked = (unitSaved || materialsUnlocked) && !unitEditMode;
  const showMaterialsSection = unitSaved || materialsUnlocked;

  useEffect(() => {
    if (unitSaved) {
      setMaterialsUnlocked(true);
    }
  }, [unitSaved]);

  const handleUnitEdit = () => {
    setUnitEditMode(true);
    setMaterialsUnlocked(false);
  };

  const { saveTransportUnitValue, saveUnitValue } = usePackagingUnitSave({
    fieldName,
    packagingType,
    unitSizes,
    palletWidth,
    palletLength,
    changeUnitsType,
    setValue,
    updatePackingDetails,
    isSaveBtnClicked,
    clickedPackagingUnit,
    setIsSaveBtnClicked,
    setClickedPackagingUnit,
    setUnitEditMode,
    onUnitSaveInitiated: () => {
      setMaterialsUnlocked(true);
      setAccordionEnabled(true);
    }
  });

  const palletWidthData = watch("palletWidth");
  const palletLengthData = watch("palletLength");
  const palletUnitData = watch(fieldName);
  const palletUnitNumeric = Number(palletUnitData) || 0;

  const saveTransportUnitDataValidation = useMemo(() => {
    if (
      unitSizes === palletUnitNumeric &&
      palletLength === palletLengthData &&
      palletWidth === palletWidthData
    ) {
      return false;
    }
    return palletUnitNumeric > 0 && palletWidthData > 0 && palletLengthData > 0;
  }, [palletUnitNumeric, palletWidthData, palletLengthData, unitSizes, palletLength, palletWidth]);

  const enableOtherUnitSaving = useMemo(() => {
    return (
      (palletUnitNumeric > 0 && unitSizes !== palletUnitNumeric) ||
      (packagingType === PackagingType.SINGLE_UNIT_PACKAGING &&
        (unitSizeWeightUnit ?? 0) > 0 &&
        weightUnit !== unitSizeWeightUnit)
    );
  }, [palletUnitNumeric, unitSizes, packagingType, weightUnit, unitSizeWeightUnit]);

  const handleClick = useCallback(() => {
    if (!packagingName) return;
    setAccordionEnabled(prev => !prev);
  }, [packagingName]);

  const canSavePackagingUnit =
    !!palletUnitNumeric &&
    (fieldName ? !(errorsUnit as Record<string, unknown>)?.[fieldName] : true) &&
    !lockStatus &&
    !isLoading;

  const canSaveTransportUnit =
    palletUnitNumeric > 0 &&
    palletWidthData > 0 &&
    palletLengthData > 0 &&
    !errorsUnit?.palletWidth &&
    !errorsUnit?.palletLength &&
    (fieldName ? !(errorsUnit as Record<string, unknown>)?.[fieldName] : true) &&
    !lockStatus &&
    !isLoading;

  // Prefer form value so badge stays in sync during edit / pending refetch.
  const displayUnitSize = palletUnitNumeric > 0 ? palletUnitNumeric : unitSizes;

  const unitBadge = useMemo(() => {
    if ((!unitSaved && !materialsUnlocked) || !levelMeta) return undefined;
    if (packagingType === PackagingType.SINGLE_UNIT_PACKAGING) {
      const activeUnit = unitSizeWeightUnit ?? weightUnit;
      const unitLabel =
        WEIGHT_UNIT_TYPES_OPTIONs_WITH_GRAM.find(u => u.value === activeUnit)?.text || "";
      return `${levelMeta.badgePrefix}: ${displayUnitSize} ${unitLabel}`;
    }
    return `${levelMeta.badgePrefix}: ${displayUnitSize}`;
  }, [
    unitSaved,
    materialsUnlocked,
    levelMeta,
    displayUnitSize,
    packagingType,
    unitSizeWeightUnit,
    weightUnit
  ]);

  const totalWeightUnit = useMemo(() => {
    if (packagingType === PackagingType.SINGLE_UNIT_PACKAGING) {
      const activeUnit = unitSaved ? weightUnit : unitSizeWeightUnit ?? weightUnit;
      return WEIGHT_UNIT_TYPES_OPTIONs_WITH_GRAM.find(u => u.value === activeUnit)?.text || "";
    }
    return undefined;
  }, [packagingType, unitSaved, weightUnit, unitSizeWeightUnit]);

  const displayTotalWeight = useMemo(() => {
    const activeUnit = unitSaved ? weightUnit : unitSizeWeightUnit ?? weightUnit;

    if (packagingType === PackagingType.SINGLE_UNIT_PACKAGING) {
      if (displayUnitSize > 0) {
        const totalKg = calculateBaseUnitTotalWeightKg(
          displayUnitSize,
          Number(unitSizeWeightUnit ?? weightUnit),
          packagingData
        );
        return fromKg(totalKg, normalizeWasteUnit(activeUnit));
      }
      if (totalWeightOfItem > 0) {
        return fromKg(totalWeightOfItem, normalizeWasteUnit(activeUnit));
      }
    }

    return totalWeightOfItem;
  }, [
    totalWeightOfItem,
    packagingType,
    displayUnitSize,
    packagingData,
    unitSaved,
    weightUnit,
    unitSizeWeightUnit
  ]);

  const sectionProps = useMemo<PackagingLevelSectionProps>(
    () => ({
      title: levelMeta?.title ?? "",
      badge: unitBadge,
      expanded: accordionEnabled,
      disabled: !packagingName,
      onToggle: handleClick
    }),
    [levelMeta?.title, unitBadge, accordionEnabled, packagingName, handleClick]
  );

  const unitSectionProps = useMemo<PackagingLevelUnitSectionProps | null>(() => {
    if (!levelMeta) return null;

    return {
      packagingType,
      fieldName,
      levelMeta,
      control,
      unitFieldsLocked,
      showMaterialsSection,
      unitGateMessage: levelMeta?.unitGateMessage ?? "",
      lockStatus: lockStatus || isLoading,
      onUnitEdit: handleUnitEdit,
      saveUnitValue,
      saveTransportUnitValue,
      enableOtherUnitSaving,
      saveTransportUnitDataValidation,
      canSavePackagingUnit,
      canSaveTransportUnit,
      palletUnitNumeric,
      palletWidthData,
      palletLengthData,
      unitSizes,
      palletLength,
      palletWidth,
      weightUnit,
      unitSizeWeightUnit,
      onUnitSizeWeightUnitChange: setUnitSizeWeightUnit,
      totalWeightLabel: `Total Weight of ${levelMeta?.type ?? ""}:`,
      displayTotalWeight,
      totalWeightUnit
    };
  }, [
    packagingType,
    fieldName,
    levelMeta,
    control,
    unitFieldsLocked,
    showMaterialsSection,
    lockStatus,
    isLoading,
    enableOtherUnitSaving,
    saveTransportUnitDataValidation,
    canSavePackagingUnit,
    canSaveTransportUnit,
    palletUnitNumeric,
    palletWidthData,
    palletLengthData,
    unitSizes,
    palletLength,
    palletWidth,
    weightUnit,
    unitSizeWeightUnit,
    setUnitSizeWeightUnit,
    displayTotalWeight,
    totalWeightUnit,
    saveUnitValue,
    saveTransportUnitValue
  ]);

  const openAccordion = () => setAccordionEnabled(true);

  return {
    levelMeta,
    showMaterialsSection,
    sectionProps,
    unitSectionProps,
    openAccordion
  };
}
